import { useEffect, useMemo, useState } from "react";
import { Radio } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  Button,
  Input,
  Message,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
} from "@/frontend/components/common";
import {
  fixtureService,
  matchTeamService,
  subCategoryService,
  tournamentFormatService,
} from "@/frontend/services";
import type {
  MatchTeamResponseDTO,
  SubCategoryResponseDTO,
  TournamentFormatResponseDTO,
} from "@/shared/dto";
import type { TournamentStageInfo } from "../types";

interface FixtureImportModalProps {
  open: boolean;
  tournamentId: string;
  seasonId: string;
  tournamentStages: TournamentStageInfo[];
  currentStageOrder?: number;
  onCancel: () => void;
  onSuccess: () => void;
}

interface CatalogMatchRow {
  key: string;
  tournamentFormatId: string;
  eventName: string;
  subCategoryId?: string;
  subCategoryName: string;
  matchOrder: number;
  homeTeamName: string;
  awayTeamName: string;
  dateLabel: string;
}

type ImportMode = "category" | "matches";

async function fetchAllTournamentFormats(params?: any): Promise<TournamentFormatResponseDTO[]> {
  const firstPage = await tournamentFormatService.getPaginate({
    page: 1,
    pageSize: 100,
    ...params,
  });
  if (!firstPage.success || !firstPage.data) {
    return [];
  }

  const formats = [...firstPage.data.data];
  for (let page = 2; page <= firstPage.data.pagination.totalPages; page += 1) {
    const nextPage = await tournamentFormatService.getPaginate({
      page,
      pageSize: 100,
      ...params,
    });
    if (nextPage.success && nextPage.data) {
      formats.push(...nextPage.data.data);
    }
  }

  return formats;
}

async function fetchAllMatchTeams(params?: any): Promise<MatchTeamResponseDTO[]> {
  const firstPage = await matchTeamService.getPaginate({
    page: 1,
    pageSize: 100,
    ...params,
  });
  if (!firstPage.success || !firstPage.data) {
    return [];
  }

  const teams = [...firstPage.data.data];
  for (let page = 2; page <= firstPage.data.pagination.totalPages; page += 1) {
    const nextPage = await matchTeamService.getPaginate({
      page,
      pageSize: 100,
      ...params,
    });
    if (nextPage.success && nextPage.data) {
      teams.push(...nextPage.data.data);
    }
  }

  return teams;
}

async function fetchAllSubCategories(): Promise<SubCategoryResponseDTO[]> {
  const firstPage = await subCategoryService.getPaginate({ page: 1, pageSize: 100 });
  if (!firstPage.success || !firstPage.data) {
    return [];
  }

  const categories = [...firstPage.data.data];
  for (let page = 2; page <= firstPage.data.pagination.totalPages; page += 1) {
    const nextPage = await subCategoryService.getPaginate({ page, pageSize: 100 });
    if (nextPage.success && nextPage.data) {
      categories.push(...nextPage.data.data);
    }
  }

  return categories;
}

function formatCatalogDate(dateValue?: string): string {
  if (!dateValue) {
    return "Sin fecha";
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return "Sin fecha";
  }

  return parsed.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function FixtureImportModal({
  open,
  tournamentId,
  seasonId,
  tournamentStages,
  currentStageOrder,
  onCancel,
  onSuccess,
}: FixtureImportModalProps) {
  const [mode, setMode] = useState<ImportMode>("category");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formats, setFormats] = useState<TournamentFormatResponseDTO[]>([]);
  const [teams, setTeams] = useState<MatchTeamResponseDTO[]>([]);
  const [categories, setCategories] = useState<SubCategoryResponseDTO[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedMatchKeys, setSelectedMatchKeys] = useState<string[]>([]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Initial load: Only categories
  useEffect(() => {
    if (!open) {
      return;
    }

    setLoading(true);
    fetchAllSubCategories()
      .then((loadedCategories) => {
        setCategories(loadedCategories);
      })
      .catch(() => {
        Message.error("No se pudieron cargar las categorías");
      })
      .finally(() => setLoading(false));
  }, [open]);

  // Load formats and teams dynamically in category mode
  useEffect(() => {
    if (!open || mode !== "category") {
      return;
    }

    if (selectedCategoryIds.length === 0) {
      setFormats([]);
      setTeams([]);
      return;
    }

    let active = true;
    setLoading(true);

    const subCategoryId = selectedCategoryIds.join(",");
    Promise.all([
      fetchAllTournamentFormats({ subCategoryId }),
      fetchAllMatchTeams({ subCategoryId }),
    ])
      .then(([loadedFormats, loadedTeams]) => {
        if (!active) return;
        setFormats(loadedFormats);
        setTeams(loadedTeams);
      })
      .catch(() => {
        Message.error("No se pudieron cargar los partidos de las categorías seleccionadas");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, mode, selectedCategoryIds]);

  // Load formats and teams dynamically in search/matches mode
  useEffect(() => {
    if (!open || mode !== "matches") {
      return;
    }

    const trimmedSearch = debouncedSearchTerm.trim();
    if (trimmedSearch.length < 2) {
      setFormats([]);
      setTeams([]);
      return;
    }

    let active = true;
    setLoading(true);

    const searchParams: any = { search: trimmedSearch };
    if (selectedCategoryIds.length > 0) {
      searchParams.subCategoryId = selectedCategoryIds.join(",");
    }

    fetchAllTournamentFormats(searchParams)
      .then(async (loadedFormats) => {
        if (!active) return;

        // Collect all unique team IDs from matches
        const teamIds = new Set<string>();
        loadedFormats.forEach((format) => {
          const configMatches = Array.isArray(format.config?.matches)
            ? format.config.matches
            : [];
          configMatches.forEach((match) => {
            if (match.homeTeamId) teamIds.add(match.homeTeamId);
            if (match.awayTeamId) teamIds.add(match.awayTeamId);
          });
        });

        let loadedTeams: MatchTeamResponseDTO[] = [];
        try {
          const [teamsById, teamsByName] = await Promise.all([
            teamIds.size > 0
              ? fetchAllMatchTeams({ id: Array.from(teamIds).join(",") })
              : Promise.resolve([]),
            fetchAllMatchTeams({ name: trimmedSearch }),
          ]);
          const teamMap = new Map<string, MatchTeamResponseDTO>();
          [...teamsById, ...teamsByName].forEach((team) => {
            teamMap.set(team.id, team);
          });
          loadedTeams = Array.from(teamMap.values());
        } catch (err) {
          console.error("Error loading teams for search results", err);
        }

        if (!active) return;
        setFormats(loadedFormats);
        setTeams(loadedTeams);
      })
      .catch(() => {
        Message.error("No se pudieron cargar los partidos de la búsqueda");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, mode, debouncedSearchTerm, selectedCategoryIds]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const defaultStage =
      tournamentStages.find((stage) => stage.stageOrder === currentStageOrder) ??
      tournamentStages[0];
    setSelectedStageId(defaultStage?.id);
  }, [open, tournamentStages, currentStageOrder]);

  useEffect(() => {
    if (!open) {
      setMode("category");
      setSelectedCategoryIds([]);
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setSelectedMatchKeys([]);
      setFormats([]);
      setTeams([]);
      setCategories([]);
    }
  }, [open]);

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    teams.forEach((team) => map.set(team.id, team.name));
    return map;
  }, [teams]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((category) => map.set(category.id, category.category));
    return map;
  }, [categories]);



  const allMatches = useMemo<CatalogMatchRow[]>(() => {
    return formats.flatMap((format) => {
      const configMatches = Array.isArray(format.config?.matches)
        ? format.config.matches
        : [];
      const subCategoryId = format.config?.subCategoryId;
      const subCategoryName = subCategoryId
        ? categoryNameById.get(subCategoryId) ?? "Sin categoria"
        : "Sin categoria";

      return configMatches.map((match, index) => ({
        key: `${format.id}:${Number(match.order ?? index + 1)}`,
        tournamentFormatId: format.id,
        eventName: format.name,
        subCategoryId,
        subCategoryName,
        matchOrder: Number(match.order ?? index + 1),
        homeTeamName: teamNameById.get(match.homeTeamId) ?? "Equipo local",
        awayTeamName: teamNameById.get(match.awayTeamId) ?? "Equipo visitante",
        dateLabel: formatCatalogDate(match.date),
      }));
    });
  }, [categoryNameById, formats, teamNameById]);

  const categoryMatches = useMemo(() => {
    if (selectedCategoryIds.length === 0) {
      return [];
    }

    return allMatches.filter(
      (match) =>
        Boolean(match.subCategoryId) &&
        selectedCategoryIds.includes(match.subCategoryId!),
    );
  }, [allMatches, selectedCategoryIds]);

  const filteredMatches = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return allMatches.filter((match) => {
      if (
        selectedCategoryIds.length > 0 &&
        (!match.subCategoryId ||
          !selectedCategoryIds.includes(match.subCategoryId))
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [
        match.eventName,
        match.subCategoryName,
        match.homeTeamName,
        match.awayTeamName,
        `partido ${match.matchOrder}`,
      ].some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [allMatches, searchTerm, selectedCategoryIds]);

  const selectedMatches = useMemo(() => {
    return allMatches.filter((match) => selectedMatchKeys.includes(match.key));
  }, [allMatches, selectedMatchKeys]);

  const stageOptions = tournamentStages.map((stage) => ({
    label: stage.name,
    value: stage.id,
  }));

  const categoryOptions = categories.map((category) => ({
    label: category.category,
    value: category.id,
  }));

  const columns: ColumnsType<CatalogMatchRow> = [
    {
      title: "Categoria",
      dataIndex: "subCategoryName",
      key: "subCategoryName",
      width: 180,
      render: (value: string) => <Tag>{value}</Tag>,
    },
    {
      title: "Evento",
      dataIndex: "eventName",
      key: "eventName",
      width: 220,
    },
    {
      title: "Partido",
      key: "match",
      render: (_, record) => `${record.homeTeamName} vs ${record.awayTeamName}`,
    },
    {
      title: "Orden",
      dataIndex: "matchOrder",
      key: "matchOrder",
      width: 90,
      align: "center",
    },
    {
      title: "Fecha",
      dataIndex: "dateLabel",
      key: "dateLabel",
      width: 140,
    },
  ];

  const handleImport = async () => {
    const matchesToImport = mode === "category" ? categoryMatches : selectedMatches;
    const uniqueMatchesToImport = Array.from(
      new Map(matchesToImport.map((match) => [match.key, match])).values(),
    );

    if (uniqueMatchesToImport.length === 0) {
      Message.error(
        mode === "category"
          ? "Selecciona una o varias categorias con partidos"
          : "Selecciona al menos un partido",
      );
      return;
    }

    try {
      setSaving(true);
      const response = await fixtureService.importFromCatalog({
        tournamentId,
        seasonId,
        stageId: selectedStageId ?? null,
        matchSelections: uniqueMatchesToImport.map((match) => ({
          tournamentFormatId: match.tournamentFormatId,
          matchOrder: match.matchOrder,
        })),
      });

      if (!response.success || !response.data) {
        Message.error(response.error ?? "No se pudieron importar los partidos");
        return;
      }

      const { createdCount, skippedCount } = response.data;
      Message.success(
        skippedCount > 0
          ? `Se importaron ${createdCount} partido(s) y se omitieron ${skippedCount}`
          : `Se importaron ${createdCount} partido(s) correctamente`,
      );
      onSuccess();
    } catch (error) {
      Message.error("No se pudieron importar los partidos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Agregar partidos al torneo"
      open={open}
      onCancel={onCancel}
      onOk={handleImport}
      okText="Agregar partidos"
      cancelText="Cancelar"
      confirmLoading={saving}
      width={1000}
      destroyOnHidden
    >
      {loading ? (
        <div className="py-12 flex justify-center">
          <Spin size="large" tip="Cargando catalogo de partidos..." />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Modo de importacion
              </p>
              <Radio.Group
                value={mode}
                onChange={(event) => {
                  setMode(event.target.value as ImportMode);
                  setSelectedMatchKeys([]);
                }}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="category">Por categoria</Radio.Button>
                <Radio.Button value="matches">Por partido</Radio.Button>
              </Radio.Group>
            </div>

            <div className="w-full md:w-72">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Fase del torneo
              </p>
              <Select
                allowClear
                placeholder="Sin fase"
                value={selectedStageId}
                onChange={(value) => setSelectedStageId(value)}
                options={stageOptions}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Categorias
              </p>
              <Select
                mode="multiple"
                allowClear
                showSearch
                size="large"
                placeholder={
                  mode === "category"
                    ? "Selecciona una o varias categorias"
                    : "Filtra por una o varias categorias"
                }
                notFoundContent="No hay categorias con partidos para mostrar."
                value={selectedCategoryIds}
                onChange={(value) => {
                  setSelectedCategoryIds((value as string[]) ?? []);
                  setSelectedMatchKeys([]);
                }}
                options={categoryOptions}
                className="w-full"
                maxTagCount="responsive"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              <p className="mt-2 text-xs text-gray-500">
                {categoryOptions.length === 0
                  ? "No hay categorias con partidos disponibles."
                  : selectedCategoryIds.length > 0
                  ? `${selectedCategoryIds.length} categoria(s) seleccionada(s)`
                  : "Puedes seleccionar varias categorias a la vez."}
              </p>
            </div>

            {mode === "matches" && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Buscar partido
                </p>
                <Input
                  placeholder="Busca por evento o nombre de equipos"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
            )}
          </div>

          {mode === "category" ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                {selectedCategoryIds.length > 0
                  ? categoryMatches.length > 0
                    ? `Se agregarán ${categoryMatches.length} partido(s) de ${selectedCategoryIds.length} categoria(s) seleccionada(s).`
                    : "No hay partidos para mostrar en las categorias seleccionadas."
                  : "Selecciona una o varias categorias para importar todos sus partidos."}
              </div>

              <Table<CatalogMatchRow>
                columns={columns}
                dataSource={categoryMatches}
                rowKey="key"
                pagination={{ pageSize: 7, hideOnSinglePage: true }}
                locale={{
                  emptyText: selectedCategoryIds.length > 0
                    ? "No hay partidos para mostrar."
                    : "Selecciona una o varias categorias para ver sus partidos.",
                }}
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedMatches.length} partido(s) seleccionado(s)
                </span>
                {selectedMatchKeys.length > 0 && (
                  <Button onClick={() => setSelectedMatchKeys([])}>
                    Limpiar seleccion
                  </Button>
                )}
              </div>

              <Table<CatalogMatchRow>
                columns={columns}
                dataSource={filteredMatches}
                rowKey="key"
                rowSelection={{
                  selectedRowKeys: selectedMatchKeys,
                  onChange: (keys) => setSelectedMatchKeys(keys as string[]),
                }}
                pagination={{ pageSize: 7, showSizeChanger: true }}
                locale={{ emptyText: "No hay partidos que coincidan con la busqueda." }}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
